import React, { createContext, useContext, useState, useCallback } from 'react';

export interface RestaurantCartItem {
  menuItemId: string;
  name: string;
  price: number;
  image?: string;
  quantity: number;
}

interface RestaurantCartContextType {
  items: RestaurantCartItem[];
  restaurantId: string | null;
  restaurantName: string | null;
  addItem: (item: Omit<RestaurantCartItem, 'quantity'>, restaurantId: string, restaurantName: string) => void;
  removeItem: (menuItemId: string) => void;
  updateQuantity: (menuItemId: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const RestaurantCartContext = createContext<RestaurantCartContextType | undefined>(undefined);

export const RestaurantCartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<RestaurantCartItem[]>([]);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string | null>(null);

  const addItem = useCallback((item: Omit<RestaurantCartItem, 'quantity'>, rId: string, rName: string) => {
    // If adding from a different restaurant, clear cart first
    if (restaurantId && restaurantId !== rId) {
      setItems([{ ...item, quantity: 1 }]);
      setRestaurantId(rId);
      setRestaurantName(rName);
      return;
    }
    setRestaurantId(rId);
    setRestaurantName(rName);
    setItems(prev => {
      const existing = prev.find(i => i.menuItemId === item.menuItemId);
      if (existing) {
        return prev.map(i => i.menuItemId === item.menuItemId ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  }, [restaurantId]);

  const removeItem = useCallback((menuItemId: string) => {
    setItems(prev => {
      const next = prev.filter(i => i.menuItemId !== menuItemId);
      if (next.length === 0) { setRestaurantId(null); setRestaurantName(null); }
      return next;
    });
  }, []);

  const updateQuantity = useCallback((menuItemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(menuItemId);
    } else {
      setItems(prev => prev.map(i => i.menuItemId === menuItemId ? { ...i, quantity } : i));
    }
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
    setRestaurantId(null);
    setRestaurantName(null);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <RestaurantCartContext.Provider value={{ items, restaurantId, restaurantName, addItem, removeItem, updateQuantity, clearCart, totalItems, totalPrice }}>
      {children}
    </RestaurantCartContext.Provider>
  );
};

export const useRestaurantCart = () => {
  const ctx = useContext(RestaurantCartContext);
  if (!ctx) throw new Error('useRestaurantCart must be used within RestaurantCartProvider');
  return ctx;
};
